import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

const HOWTO_SCHEMA = {
  '@type': 'HowTo',
  name: 'How to Submit CE Hours to ADRE',
  description: 'Step-by-step instructions for submitting Arizona real estate CE certificates through the ADRE Licensee Login portal.',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Sign in to ADRE Licensee Login', text: 'Go to ptl.az.gov/dre and sign in.' },
    { '@type': 'HowToStep', position: 2, name: 'Open the CE section', text: 'Open the continuing education section of your license record.' },
    { '@type': 'HowToStep', position: 3, name: 'Enter each course', text: 'Enter the course number, start date, and end date exactly as printed on the certificate.' },
    { '@type': 'HowToStep', position: 4, name: 'Keep the certificate file', text: 'ADRE can audit — you are responsible for keeping proof.' },
    { '@type': 'HowToStep', position: 5, name: 'Verify category progress', text: 'Check your category progress on the same screen. Green across every category means you are ready to renew.' },
  ],
};

const RELATED = [
  { url: '/arizona/how-to-renew', title: 'How to Renew, Step by Step', desc: 'The full renewal in five steps: CE, Licensee Login, Renew, pay.' },
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'Categories, hours, and the 2025 rule changes.' },
  { url: '/when-does-my-arizona-license-expire', title: 'When Does My AZ License Expire?', desc: 'How to check your exact renewal date in 60 seconds.' },
  { url: '/how-fast-can-i-finish-arizona-ce', title: 'How Fast Can You Finish 24 Hours?', desc: 'The 9-hour daily cap explained.' },
];

export default function SubmitCePage() {
  useSEO({
    title: 'How to Submit CE Hours to ADRE (Step-by-Step)',
    description: 'Your school doesn\'t report CE to ADRE — you do. Exactly how to enter certificates in the Licensee Login portal and fix common errors.',
    url: canonicalUrl('/how-to-submit-ce-to-adre'),
    jsonLd: guidePageSchema(HOWTO_SCHEMA, {
      title: 'Submit CE to ADRE',
      url: canonicalUrl('/how-to-submit-ce-to-adre'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="Submit to ADRE"
      title="How to Submit Your CE Hours to ADRE (Most Agents Get This Wrong)"
      subtitle="Here's the single most misunderstood fact about Arizona CE: your school does not report your course completions to ADRE. You do."
      relatedGuides={RELATED}
    >
      <p>Every certificate, entered by you, through the Licensee Login portal. If you've been assuming your courses "count" automatically — <strong>check your portal today.</strong></p>

      <h2>What you need from each course</h2>
      <p>Your certificate shows three things ADRE requires:</p>
      <ul>
        <li><strong>Course Number</strong></li>
        <li><strong>Course Start Date</strong></li>
        <li><strong>Course End Date</strong></li>
      </ul>
      <p>Any ADRE-approved school must give you this certificate when you pass (Steady Education issues yours instantly on completion).</p>

      <h2>Step-by-step</h2>
      <ol>
        <li>Go to the <strong>ADRE Licensee Login at ptl.az.gov/dre</strong> and sign in.</li>
        <li>Open the continuing education section of your license record.</li>
        <li>Enter each course: course number, start date, end date, exactly as printed on the certificate.</li>
        <li>Keep the certificate file — ADRE can audit, and you're responsible for proof.</li>
        <li>Check your category progress on the same screen. Green across every category = ready to renew.</li>
      </ol>

      <p>You can <strong>submit anytime</strong> during your two-year renewal period — submit each course as you finish it rather than batching 8 entries the night before your deadline.</p>

      <h2>The two errors everyone hits</h2>

      <p><strong>"Course taken outside allowable date range"</strong> — either the course predates the start of your current two-year window, or your school printed the wrong course number and needs to issue a corrected certificate.</p>

      <p><strong>"I have 24+ hours, why can't I renew?"</strong> — total hours don't matter if a mandatory category is missing. Surplus hours flow to elective; they never fill a different mandatory category. The portal shows exactly which category is short.</p>

      <div style={inlineCta}>
        <strong>Instant certificates included.</strong> Steady Education issues your certificate the moment you pass each exam — submit to ADRE the same night.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>Browse courses →</Link>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 32 }}>
        Source: ADRE Education Division FAQ, azre.gov.
      </p>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
